import {
  ConfirmContactVerificationOutcome,
  ContactVerificationChannel,
  StartContactVerificationOutcome,
} from '../../_data/types'

/**
 * Turns the outcome of a send request into something worth reading.
 *
 * A refusal is reported in the renter's terms rather than the API's: they need
 * to know whether to check their inbox, wait, or fix what they typed.
 *
 * @param {StartContactVerificationOutcome} outcome - what the backend reported
 * @param {ContactVerificationChannel} channel - which contact method was used
 * @param {string} maskedDestination - the masked destination the code went to
 * @param {number} retryAfterSeconds - how long until another send is allowed
 * @returns {string} the message to show
 */
const resolveSendMessage = (
  outcome: StartContactVerificationOutcome,
  channel: ContactVerificationChannel,
  maskedDestination: string,
  retryAfterSeconds: number,
): string => {
  if (outcome === 'sent') {
    return `We sent a code to ${maskedDestination}. It is good for the next 10 minutes.`
  }

  if (outcome === 'cooldown') {
    return `We just sent one. You can ask for another in ${retryAfterSeconds} seconds.`
  }

  if (outcome === 'send-limit') {
    return 'That is as many codes as we can send for now. Give it a few minutes and try again.'
  }

  if (channel === 'email') {
    return 'That does not look like an email address we can reach.'
  }

  return 'That does not look like a phone number we can text.'
}

/**
 * Turns the outcome of a confirm request into something worth reading.
 *
 * @param {ConfirmContactVerificationOutcome} outcome - what the backend reported
 * @param {number} attemptsRemaining - how many tries are left on this code
 * @returns {string} the message to show
 */
const resolveConfirmMessage = (
  outcome: ConfirmContactVerificationOutcome,
  attemptsRemaining: number,
): string => {
  if (outcome === 'verified') {
    return 'Confirmed. That badge is on your QuickPass now.'
  }

  if (outcome === 'invalid-code') {
    if (attemptsRemaining <= 0) {
      return 'That code was not right, and that was the last try. Send a new one.'
    }

    return `That code was not right. ${attemptsRemaining} ${attemptsRemaining === 1 ? 'try' : 'tries'} left.`
  }

  if (outcome === 'expired') {
    return 'That code has expired. Send yourself a fresh one.'
  }

  if (outcome === 'too-many-attempts') {
    return 'Too many tries on that code. Send a new one to start over.'
  }

  return 'We could not find a code in progress. Send yourself a new one.'
}

/**
 * The label for the button that opens the row.
 *
 * @param {boolean} isVerified - whether the channel is already confirmed
 * @returns {string} the button label
 */
const resolveOpenLabel = (isVerified: boolean): string => {
  if (isVerified) {
    return 'Change'
  }

  return 'Verify'
}

export { resolveSendMessage, resolveConfirmMessage, resolveOpenLabel }
